import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, Truck, Clock, ArrowRight, Instagram } from "lucide-react";
import { useSiteTexts, t } from "../contexts/SiteTextsContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TierDiscountStrip from "../components/TierDiscountStrip";
import RecentlyViewed from "../components/RecentlyViewed";
import SaveHeart from "../components/SaveHeart";
import SEO, { LOCAL_BUSINESS_JSONLD, ORGANIZATION_JSONLD } from "../components/SEO";
import { consumeBackNavigation, restoreScrollPosition } from "../components/ScrollToTop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InstagramCarousel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    fetch(`${API}/social-posts?active_only=true&limit=9`)
      .then(r => r.json())
      .then(d => setPosts(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (ref) ref.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [posts]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  if (loading || posts.length === 0) return null;

  return (
    <section className="px-6 py-14 lg:px-16 lg:py-24 bg-white relative overflow-hidden" data-testid="instagram-carousel-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-10 lg:mb-12">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Instagram className="w-4 h-4 text-[#C4A4A0]" strokeWidth={1.5} />
            <p className="text-[10px] text-[#C4A4A0] tracking-[0.3em] uppercase">Follow Our Journey</p>
          </div>
          <h2 className="text-2xl lg:text-4xl text-[#282828] font-light">As Seen in Real Celebrations</h2>
        </div>
        <div className="relative group">
          {canScrollLeft && (
            <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-[#C4A4A0] hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {canScrollRight && (
            <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-[#C4A4A0] hover:text-white">
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {posts.map((post) => (
              <a
                key={post.id}
                href={post.instagram_link || "#"}
                target={post.instagram_link ? "_blank" : "_self"}
                rel="noopener noreferrer"
                className={`flex-none w-64 lg:w-72 snap-start group/card ${!post.instagram_link ? 'pointer-events-none' : ''}`}
              >
                <div className="relative aspect-square overflow-hidden bg-[#FAF8F0]">
                  <img src={post.image_url} alt={post.caption || "Indian outfit rental Melbourne"} className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-[#3D2B1F]/0 group-hover/card:bg-[#3D2B1F]/40 transition-all flex items-center justify-center">
                    {post.instagram_link && <Instagram className="w-8 h-8 text-white opacity-0 group-hover/card:opacity-100 transition-opacity" strokeWidth={1.5} />}
                  </div>
                </div>
                {post.caption && <p className="mt-2.5 text-[12px] text-[#282828]/60 line-clamp-2 font-light">{post.caption}</p>}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ShopTheLook() {
  const [looks, setLooks] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const sectionRef = useRef(null);

  // Lazy-load: only fetch when section scrolls into view
  useEffect(() => {
    if (hasLoaded) return;
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetch(`${API}/ai/looks?limit=5`).then(r => r.json()).then(d => setLooks((d.looks || []).slice(0, 5))).catch(() => {});
          setHasLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasLoaded]);

  if (!hasLoaded || looks.length === 0) return <div ref={sectionRef} style={{ minHeight: 1 }} />;
  const hero = looks[0];
  const _OUTFIT_CATS_HOME = ['lehengas', 'sarees', 'anarkalis & shararas', 'indo western', 'indo-western'];
  const heroItem = hero.items?.find(i => _OUTFIT_CATS_HOME.includes((i.category || '').toLowerCase()))
    || hero.items?.find(i => i.id === hero.hero_product_id) || hero.items?.[0];
  const rest = looks.slice(1, 5);
  const itemCount = hero.items?.length || 0;
  const tierDiscount = itemCount >= 4 ? 20 : itemCount === 3 ? 15 : itemCount === 2 ? 10 : 0;
  const discountedPrice = tierDiscount > 0 ? Math.round(hero.total_price_4_day * (1 - tierDiscount / 100)) : hero.total_price_4_day;

  return (
    <section className="py-12 lg:py-16 px-4 lg:px-10 bg-white" data-testid="shop-the-look">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Shop the Look</h2>
            <p className="text-[11px] text-[#676664] mt-1">Complete outfits curated for every occasion</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-[420px]">
          {/* Hero Look — large */}
          <Link to={`/looks/${hero.id}`} className="lg:col-span-7 group relative overflow-hidden bg-[#f5f3f0] aspect-[16/10] lg:aspect-auto lg:h-full" data-testid="hero-look-card">
            {(hero.cover_image || heroItem?.image) && <img src={hero.cover_image || heroItem?.image} alt={`${hero.name} – Indian outfit hire Melbourne`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: `${hero.cover_focus_x || 50}% ${hero.cover_focus_y || 50}%` }} loading="lazy" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            {tierDiscount > 0 && (
              <div className="absolute top-4 right-4 bg-[#9A7B4F] px-3 py-1.5"><span className="text-[10px] tracking-wider uppercase text-white font-medium">Save {tierDiscount}%</span></div>
            )}
            <div className="absolute bottom-0 inset-x-0 p-5 lg:p-8">
              {hero.occasion && <span className="text-[9px] tracking-[3px] uppercase text-white/60 mb-1 block">{hero.occasion}</span>}
              <h3 className="text-xl lg:text-2xl text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{hero.name}</h3>
              <p className="text-[11px] text-white/70 mt-1 line-clamp-1">{hero.items?.map(i => i.name).join(" + ")}</p>
              <div className="flex items-center gap-4 mt-3">
                <span className="bg-white text-[#282828] px-5 py-2 text-[10px] tracking-wider uppercase font-medium group-hover:bg-[#FAF8F0] transition-colors">
                  Rent This Look · ${discountedPrice}
                </span>
                <span className="text-white/50 text-[11px]">{hero.items?.length} pieces</span>
              </div>
            </div>
          </Link>
          {/* Smaller looks — flex col on desktop for guaranteed equal height */}
          <div className="lg:col-span-5 grid grid-cols-2 lg:flex lg:flex-col gap-4">
            {rest.map((look, idx) => {
              const item = look.items?.find(i => _OUTFIT_CATS_HOME.includes((i.category || '').toLowerCase()))
                || look.items?.find(i => i.id === look.hero_product_id) || look.items?.[0];
              const lookImg = look.cover_image || item?.image;
              const focusStyle = { objectPosition: `${look.cover_focus_x || 50}% ${look.cover_focus_y || 50}%` };
              return (
                <Link key={look.id} to={`/looks/${look.id}`} className={`group relative overflow-hidden bg-[#f5f3f0] aspect-[4/3] lg:aspect-auto lg:flex-1 lg:min-h-0${idx >= 2 ? ' lg:hidden' : ''}`} data-testid={`stl-card-${look.id}`}>
                  {lookImg && <img src={lookImg} alt={`${look.name} – Indian outfit hire Melbourne`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={focusStyle} loading="lazy" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-3 lg:p-4">
                    {look.occasion && <span className="text-[8px] tracking-[2px] uppercase text-white/50">{look.occasion}</span>}
                    <h3 className="text-[13px] lg:text-[15px] text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{look.name}</h3>
                    <span className="text-[10px] text-white/60">{look.items?.length} pieces · From ${look.total_price_4_day}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
        <div className="mt-6 text-center">
          <Link to="/looks" className="text-[11px] tracking-wider uppercase text-[#9A7B4F] hover:text-[#8a6b3f] inline-flex items-center gap-1.5" data-testid="view-all-looks-link">
            View All Looks <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categories, setCategories] = useState([]);
  const [outfits, setOutfits] = useState([]);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [storyImage, setStoryImage] = useState({ image_url: "", position_x: 50, position_y: 50 });
  const [categoryTree, setCategoryTree] = useState([]);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const [canScrollFeaturedLeft, setCanScrollFeaturedLeft] = useState(false);
  const [canScrollFeaturedRight, setCanScrollFeaturedRight] = useState(false);
  const navigate = useNavigate();
  const texts = useSiteTexts();
  const didRestoreScroll = useRef(false);

  useEffect(() => {
    fetch(`${API}/settings/hero-slides`).then(r => r.json()).then(d => {
      if (d.slides?.length) {
        setSlides(d.slides);
        // Preload first hero image
        const firstUrl = d.slides[0].image_url;
        if (firstUrl) {
          const link = document.createElement('link');
          link.rel = 'preload'; link.as = 'image'; link.href = firstUrl;
          document.head.appendChild(link);
        }
      } else {
        fetch(`${API}/settings/hero`).then(r => r.json()).then(h => {
          if (h.image_url) setSlides([{ id: "legacy", image_url: h.image_url, headline: h.headline, headline_italic: h.headline_italic, subheading: h.subheading, launch_text: h.launch_text, button_text: h.button_text, button_link: h.button_link, enabled: true }]);
        });
      }
    }).catch(() => {});
    fetch(`${API}/settings/categories`).then(r => r.json()).then(d => {
      setCategories(d.categories || []);
      setCategoryTree(d.categories || []);
    }).catch(() => {});
    fetch(`${API}/outfits`).then(r => r.json()).then(d => {
      const available = d.filter(o => o.available);
      const featured = available.filter(o => o.featured);
      setOutfits(featured.length > 0 ? featured : available);
      // Restore scroll after content loads (on back navigation)
      if (!didRestoreScroll.current) {
        didRestoreScroll.current = true;
        if (consumeBackNavigation()) {
          restoreScrollPosition("/");
        }
      }
    }).catch(() => {});
    fetch(`${API}/settings/story-image`).then(r => r.json()).then(d => setStoryImage(d)).catch(() => {});
  }, []);

  // Auto-rotate
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (slides.length > 1) {
      timerRef.current = setInterval(() => setCurrentSlide(c => (c + 1) % slides.length), 5000);
    }
  }, [slides.length]);

  useEffect(() => { startTimer(); return () => clearInterval(timerRef.current); }, [startTimer]);

  // Featured Pieces scroll logic
  const checkFeaturedScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollFeaturedLeft(scrollLeft > 0);
    setCanScrollFeaturedRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  useEffect(() => {
    checkFeaturedScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkFeaturedScroll);
      window.addEventListener('resize', checkFeaturedScroll);
    }
    return () => {
      if (ref) ref.removeEventListener('scroll', checkFeaturedScroll);
      window.removeEventListener('resize', checkFeaturedScroll);
    };
  }, [outfits, checkFeaturedScroll]);

  // Auto-scroll featured on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile || outfits.length <= 2) return;
    const ref = scrollRef.current;
    if (!ref) return;
    let direction = 1;
    const interval = setInterval(() => {
      const { scrollLeft, scrollWidth, clientWidth } = ref;
      if (scrollLeft >= scrollWidth - clientWidth - 5) direction = -1;
      if (scrollLeft <= 0) direction = 1;
      ref.scrollBy({ left: direction * 220, behavior: 'smooth' });
    }, 3500);
    const stopOnTouch = () => clearInterval(interval);
    ref.addEventListener('touchstart', stopOnTouch, { once: true });
    return () => { clearInterval(interval); ref.removeEventListener('touchstart', stopOnTouch); };
  }, [outfits]);

  const scrollFeatured = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  const goSlide = (idx) => { setCurrentSlide(idx); startTimer(); };
  const activeSlide = slides[currentSlide] || {};

  const catCount = categories.length;
  const catCols = catCount <= 2 ? `grid-cols-${catCount}` :
    catCount === 3 ? "grid-cols-3" :
    catCount <= 4 ? "grid-cols-2 lg:grid-cols-4" :
    catCount <= 6 ? "grid-cols-2 lg:grid-cols-3" :
    "grid-cols-2 lg:grid-cols-4";

  // Dynamic category grid: single parent → subcategories, multi parent → parent categories
  const enabledParents = categoryTree.filter(c => c.enabled !== false);
  const singleParentMode = enabledParents.length === 1 && enabledParents[0].children?.filter(c => c.enabled !== false).length > 0;
  const singleParent = singleParentMode ? enabledParents[0] : null;
  const displayCategories = singleParentMode
    ? enabledParents[0].children.filter(c => c.enabled !== false)
    : enabledParents;
  const displayCount = displayCategories.length;
  const displayCols = displayCount <= 2 ? `grid-cols-${displayCount}` :
    displayCount === 3 ? "grid-cols-3" :
    displayCount === 4 ? "grid-cols-2 lg:grid-cols-4" :
    displayCount <= 6 ? "grid-cols-2 lg:grid-cols-3" :
    "grid-cols-2 lg:grid-cols-4";

  const buildCategoryUrl = (cat) => {
    if (singleParentMode && singleParent) {
      return `/${singleParent.slug || singleParent.id}/${cat.slug || cat.id}`;
    }
    return `/${cat.slug || cat.id}`;
  };

  // Smart "Browse Collection" URL: single parent → go to parent page, multi → /catalog
  const browseUrl = singleParentMode && singleParent
    ? `/${singleParent.slug || singleParent.id}`
    : "/catalog";

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <SEO
        rawTitle="Mahra – Premium Indian Outfit Rental & Hire Melbourne"
        description="Rent designer Indian outfits in Melbourne. Lehengas, sarees, anarkalis & accessories for hire. Premium quality, affordable prices. Free delivery on orders over $175."
        path="/"
        jsonLd={[LOCAL_BUSINESS_JSONLD, ORGANIZATION_JSONLD]}
      />
      <Navbar />

      {/* === HERO CAROUSEL === */}
      <section
        className="relative w-full overflow-hidden"
        style={{ height: 'min(85vh, 800px)' }}
        onMouseEnter={() => clearInterval(timerRef.current)}
        onMouseLeave={startTimer}
        data-testid="hero-carousel"
      >
        {slides.map((slide, i) => (
          <div
            key={slide.id || i}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === currentSlide ? 1 : 0, zIndex: i === currentSlide ? 1 : 0 }}
          >
            <img
              src={slide.image_url}
              alt={`Indian outfit rental Melbourne – ${slide.headline || "Premium hire collection"}`}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: `${slide.image_position_x ?? 50}% ${slide.image_position_y ?? 50}%` }}
              onLoad={() => i === 0 && setHeroLoaded(true)}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "low"}
              decoding={i === 0 ? "sync" : "async"}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-transparent" />
          </div>
        ))}
        {slides.length === 0 && !heroLoaded && (
          <div className="absolute inset-0 bg-[#E8E0D5] animate-pulse" />
        )}
        {slides.length === 0 && heroLoaded && (
          <div className="absolute inset-0 bg-[#E8E0D5] flex items-center justify-center">
            <p className="text-[#676664] text-sm">Add hero slides in admin</p>
          </div>
        )}
        <div className="relative z-10 h-full flex items-end px-6 lg:px-16 pb-12 lg:pb-20 max-w-[1400px] mx-auto">
          <div className="max-w-lg">
            <p className="text-[10px] text-white/70 tracking-[0.3em] uppercase mb-3">{activeSlide.launch_text || ""}</p>
            <h1 className="text-3xl lg:text-6xl text-white font-light leading-[1.1] mb-2">
              {activeSlide.headline || ""}
              {activeSlide.headline_italic && (
                <span className="block italic">{activeSlide.headline_italic}</span>
              )}
            </h1>
            <p className="text-sm lg:text-base text-white/80 font-light leading-relaxed mt-4 mb-6 max-w-md">{activeSlide.subheading || ""}</p>
            <Link
              to={singleParentMode ? browseUrl : (activeSlide.button_link || browseUrl)}
              className="inline-flex items-center gap-3 bg-white text-[#282828] px-8 py-3.5 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-[#FAF8F0] transition-colors"
              data-testid="hero-cta-btn"
            >
              {activeSlide.button_text || "Browse Collection"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        {/* Slide dots */}
        {slides.length > 1 && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => goSlide(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'}`} data-testid={`hero-dot-${i}`} />
            ))}
          </div>
        )}
        {/* Arrows */}
        {slides.length > 1 && (
          <>
            <button onClick={() => goSlide((currentSlide - 1 + slides.length) % slides.length)} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><ChevronLeft className="w-5 h-5 text-white" /></button>
            <button onClick={() => goSlide((currentSlide + 1) % slides.length)} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><ChevronRight className="w-5 h-5 text-white" /></button>
          </>
        )}
      </section>

      {/* === TIER DISCOUNT STRIP === */}
      <TierDiscountStrip />

      {/* === HOW IT WORKS === */}
      <section className="border-y border-[#282828]/5">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 lg:grid-cols-4">
          {[
            { title: t(texts, "homepage_how_it_works_1_title", "Browse Our Wardrobe"), desc: t(texts, "homepage_how_it_works_1_desc", "Explore curated pieces"), href: "/catalog" },
            { title: t(texts, "homepage_how_it_works_2_title", "Choose Your Event Date"), desc: t(texts, "homepage_how_it_works_2_desc", "Select when you need it"), href: "/catalog?highlight=date" },
            { title: t(texts, "homepage_how_it_works_3_title", "Pickup or Delivery"), desc: t(texts, "homepage_how_it_works_3_desc", "Free pickup or $15 local delivery"), href: "/faq#pickup-delivery" },
            { title: t(texts, "homepage_how_it_works_4_title", "Wear & Return"), desc: t(texts, "homepage_how_it_works_4_desc", "Enjoy your event, then return it"), href: "/faq#returns" },
          ].map((step, i) => (
            <Link key={i} to={step.href} className={`block py-6 lg:py-10 px-4 lg:px-8 text-center hover:bg-[#F0E8DC]/40 transition-colors ${i < 3 ? 'border-r border-[#282828]/5' : ''} ${i === 1 ? 'border-b lg:border-b-0 border-[#282828]/5' : ''} ${i === 0 ? 'border-b lg:border-b-0 border-[#282828]/5' : ''}`} data-testid={`how-it-works-step-${i+1}`}>
              <p className="text-[12px] lg:text-[14px] text-[#282828] font-medium mb-1">{step.title}</p>
              <p className="text-[10px] lg:text-[11px] text-[#676664] font-light leading-relaxed">{step.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* === SHOP THE LOOK === */}
      <ShopTheLook />

      {/* === SHOP BY CATEGORY (dynamic) === */}
      {displayCategories.length > 0 && (
        <section className="py-14 lg:py-24 px-4 lg:px-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-10 lg:mb-12">
              <p className="text-[10px] text-[#676664] tracking-[0.3em] uppercase mb-2">Curated Collections</p>
              <h2 className="text-2xl lg:text-4xl text-[#282828] font-light">{t(texts, "homepage_category_heading", "Shop by Category")}</h2>
            </div>
            <div className={`grid ${displayCols} gap-3 lg:gap-5`}>
              {displayCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={buildCategoryUrl(cat)}
                  className="group relative overflow-hidden"
                  style={{ paddingBottom: '100%', height: 0 }}
                  data-testid={`home-cat-${cat.id}`}
                >
                  <div className="absolute inset-0 bg-[#E8E0D5]">
                    {cat.image_url && <img src={cat.image_url} alt={`${cat.name} for rent in Melbourne – Indian outfit hire`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" decoding="async" />}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors z-10" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6 z-20">
                      <p className="text-[9px] lg:text-[10px] text-white/80 tracking-[0.2em] uppercase">{cat.label}</p>
                      <p className="text-white text-sm lg:text-base font-light mt-0.5">{cat.name}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* === FEATURED PRODUCTS === */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-[10px] text-[#676664] tracking-[0.3em] uppercase mb-2">Our Collection</p>
              <h2 className="text-2xl lg:text-4xl text-[#282828] font-light">{t(texts, "homepage_featured_heading", "Featured Pieces")}</h2>
            </div>
            <Link to="/catalog" className="text-[11px] text-[#282828] tracking-[0.15em] uppercase hover:underline underline-offset-4 hidden lg:block">
              View All <ChevronRight className="w-3 h-3 inline" />
            </Link>
          </div>
          <div className="relative group">
            {canScrollFeaturedLeft && (
              <button onClick={() => scrollFeatured('left')} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 bg-white shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-[#9A7B4F] hover:text-white" data-testid="featured-scroll-left">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            {canScrollFeaturedRight && (
              <button onClick={() => scrollFeatured('right')} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 bg-white shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-[#9A7B4F] hover:text-white" data-testid="featured-scroll-right">
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          <div className="flex gap-4 lg:gap-6 overflow-x-auto pb-4 -mx-2 px-2 snap-x snap-mandatory" ref={scrollRef} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {(outfits.length > 0 ? outfits.slice(0, 8) : []).map((outfit) => (
              <div key={outfit.id} className="flex-shrink-0 w-[200px] sm:w-[240px] lg:w-[280px] snap-start group" data-testid={`featured-${outfit.id}`}>
                <Link to={`/outfit/${outfit.id}`} className="block">
                <div className="relative overflow-hidden bg-[#f7f5f4]" style={{ height: 0, paddingBottom: '133.33%' }}>
                  <img src={outfit.images?.[0]} alt={`${outfit.name} for rent in Melbourne – Indian outfit hire`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" style={{ objectPosition: `${(outfit.image_focal_points || {})[outfit.images?.[0]]?.x ?? 50}% ${(outfit.image_focal_points || {})[outfit.images?.[0]]?.y ?? 50}%` }} loading="lazy" decoding="async" onError={(e) => { e.target.style.display='none'; }} />
                  <SaveHeart outfitId={outfit.id} className="absolute top-3 right-3 w-8 h-8 bg-white/80 hover:bg-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10" size={16} dark />
                </div>
                </Link>
                <div className="pt-3">
                  <p className="text-[12px] text-[#282828] truncate">{outfit.name}</p>
                  <p className="text-[12px] text-[#676664] font-light mt-0.5">From ${outfit.price_4_day || outfit.price_per_day || 0} / rental</p>
                </div>
              </div>
            ))}
          </div>
          </div>
          <Link to="/catalog" className="block text-center text-[11px] text-[#282828] tracking-[0.15em] uppercase mt-6 lg:hidden">
            View All Pieces <ChevronRight className="w-3 h-3 inline" />
          </Link>
        </div>
      </section>

      {/* === ABOUT MAHRA === */}
      <section className="py-14 lg:py-24 px-4 lg:px-10">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
          <div className="relative overflow-hidden" style={{ height: 0, paddingBottom: '100%' }}>
            <img
              src={storyImage.image_url || "https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=800&q=80"}
              alt="About Mahra – Indian outfit rental Melbourne"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: `${storyImage.position_x ?? 50}% ${storyImage.position_y ?? 50}%` }}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="lg:max-w-md">
            <p className="text-[10px] text-[#676664] tracking-[0.3em] uppercase mb-4">Our Story</p>
            <h2 className="text-2xl lg:text-4xl text-[#282828] font-light leading-tight mb-6">
              Curated with Care,<br /><span className="italic">Shared with Love</span>
            </h2>
            <p className="text-[13px] text-[#676664] leading-relaxed mb-4">
              {t(texts, "homepage_about_text", "Mahra was born from the belief that beautiful outfits deserve more than one celebration. We curate high-quality, once-worn Indian pieces and make them available for you to borrow.")}
            </p>
            <p className="text-[13px] text-[#676664] leading-relaxed mb-8">
              Every piece in our collection has a story — and now it can be part of yours.
            </p>
            <Link to="/our-story" className="inline-flex items-center gap-2 text-[11px] text-[#282828] tracking-[0.2em] uppercase border-b border-[#282828] pb-1 hover:opacity-70 transition-opacity">
              {t(texts, "homepage_about_cta", "Read Our Story")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* === TRUST SIGNALS === */}
      <section className="border-y border-[#282828]/5 bg-white">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 divide-x divide-[#282828]/5">
          {[
            { icon: Truck, title: t(texts, "trust_1_title", "Free Local Pickup"), desc: t(texts, "trust_1_desc", "Melbourne — Oakleigh South") },
            { icon: Clock, title: t(texts, "trust_2_title", "Flexible Rentals"), desc: t(texts, "trust_2_desc", "4 or 8 day bookings") },
          ].map(({ icon: Icon, title, desc }, i) => (
            <div key={i} className="py-6 lg:py-10 px-3 lg:px-8 text-center">
              <Icon className="w-5 h-5 text-[#9A7B4F] mx-auto mb-2 lg:mb-3" strokeWidth={1.5} />
              <p className="text-[11px] lg:text-[12px] text-[#282828] font-medium mb-0.5">{title}</p>
              <p className="text-[9px] lg:text-[10px] text-[#676664] font-light">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* === INSTAGRAM CAROUSEL === */}
      <InstagramCarousel />

      {/* Recently Viewed */}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-10 pb-8">
        <RecentlyViewed />
      </div>

      {/* === SEO CONTENT BLOCK === */}
      <section className="border-t border-[#282828]/5 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-14 lg:py-20">
          <div className="max-w-3xl">
            <h2 className="text-xl lg:text-2xl text-[#282828] font-light mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t(texts, "seo_block_heading", "Premium Indian Outfit Rental & Hire in Melbourne")}</h2>
            <div className="text-[13px] text-[#676664] leading-relaxed space-y-4">
              <p>{t(texts, "seo_block_intro", "Mahra offers premium Indian outfit rental in Melbourne, making it easy to hire lehengas, sarees, and statement pieces for weddings and special occasions.")}</p>
              {t(texts, "seo_block_section1_heading", "Why Rent Indian Outfits in Melbourne?") && <h2 className="text-[15px] text-[#282828] font-medium">{t(texts, "seo_block_section1_heading", "Why Rent Indian Outfits in Melbourne?")}</h2>}
              <p>{t(texts, "seo_block_section1_text", "Melbourne's vibrant South Asian community celebrates with style, and every occasion deserves an outfit that makes a statement. Renting offers the freedom to wear premium pieces valued at hundreds of dollars for a fraction of the cost. At Mahra, every piece in our collection has been carefully sourced, professionally maintained, and styled to meet the expectations of Melbourne's most discerning clientele.")}</p>
              {t(texts, "seo_block_section2_heading", "Our Collection") && <h2 className="text-[15px] text-[#282828] font-medium">{t(texts, "seo_block_section2_heading", "Our Collection")}</h2>}
              <p>{t(texts, "seo_block_section2_text", "From intricately embroidered bridal lehengas for hire perfect for your Melbourne wedding to lightweight sarees for rent ideal for festive gatherings, our range covers every occasion and aesthetic. Our collection includes lehengas with premium fabrics and hand-embellished detailing, classic Banarasi silks and contemporary drapes, flowing anarkalis and shararas ideal for mehndi nights, modern indo-western fusion pieces, and coordinating jewellery, clutches, and potlis available for hire.")}</p>
              {t(texts, "seo_block_section3_heading", "How Indian Outfit Hire Works") && <h2 className="text-[15px] text-[#282828] font-medium">{t(texts, "seo_block_section3_heading", "How Indian Outfit Hire Works")}</h2>}
              <p>{t(texts, "seo_block_section3_text", "Renting with Mahra is simple. Browse our online collection, select your event dates, and choose between our 4-day or 8-day rental periods. We offer free local pickup from Oakleigh South and affordable delivery across Melbourne for orders over $175. Each outfit arrives freshly dry-cleaned and ready to wear — simply enjoy your event and return it when your rental period ends.")}</p>
              {t(texts, "seo_block_section4_heading", "Serving All of Melbourne") && <h2 className="text-[15px] text-[#282828] font-medium">{t(texts, "seo_block_section4_heading", "Serving All of Melbourne")}</h2>}
              <p>{t(texts, "seo_block_section4_text", "Based in Oakleigh South, we serve Melbourne's diverse communities with pride. Whether you're in the southeastern suburbs or across the city, our delivery service ensures your outfit arrives on time and in perfect condition. Our clients celebrate weddings, engagements, Diwali, and cultural events throughout Victoria.")}</p>
              <p>{t(texts, "seo_block_closing", "Take your styling further with our Build Your Look feature. Pair a statement piece with matching jewellery and a designer clutch, and enjoy bundle discounts of up to 20% when you hire multiple pieces together. Discover why Melbourne chooses Mahra for premium Indian outfit rental.")}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
